import type { NextRequest } from 'next/server';

/** Research / deployment phase used in recording paths. */
export type RecordingEnv = 'dev' | 'alpha' | 'pilot' | 'main' | 'demo';

export function parseHostname(rawHost: string): string {
  const host = rawHost.split(',')[0]?.trim().toLowerCase() ?? '';
  return host.split(':')[0] ?? '';
}

/**
 * Maps request host to recording env:
 * - dev.* / localhost / cloudflare tunnel → dev
 * - alpha.* → alpha
 * - beta.* / pilot.* → pilot (beta host kept for DNS compatibility)
 * - demo.* → demo
 * - everything else (formal site) → main
 */
export function getRecordingEnvFromHostname(hostname: string): RecordingEnv {
  const normalized = hostname.toLowerCase();
  if (normalized.startsWith('dev.')) return 'dev';
  if (normalized.startsWith('alpha.')) return 'alpha';
  if (normalized.startsWith('beta.') || normalized.startsWith('pilot.')) return 'pilot';
  if (normalized.startsWith('demo.')) return 'demo';
  if (normalized === 'localhost' || normalized === '127.0.0.1') return 'dev';
  if (normalized.endsWith('.trycloudflare.com')) return 'dev';
  return 'main';
}

export function getRecordingEnvFromRequest(req: NextRequest): RecordingEnv {
  const rawHost = req.headers.get('x-forwarded-host') ?? req.headers.get('host') ?? '';
  return getRecordingEnvFromHostname(parseHostname(rawHost));
}

export type RecordingPathOptions = {
  /** When env is pilot, optional round folder (e.g. `r1`). */
  pilotRound?: string;
};

/**
 * Path segments under `recordings/`:
 * - demo interview → `demo/{env}`
 * - study interview → `{studyId}/{env}` or `{studyId}/pilot/{round}`
 */
export function getRecordingPathSegments(
  sessionId: string,
  req: NextRequest,
  studyId?: string,
  options?: RecordingPathOptions,
): string[] {
  const env = getRecordingEnvFromRequest(req);
  if (sessionId.startsWith('demo-')) {
    return ['demo', env];
  }
  if (studyId) {
    if (env === 'pilot' && options?.pilotRound) {
      return [studyId, 'pilot', options.pilotRound];
    }
    return [studyId, env];
  }
  return [env];
}

const RECORDING_ENVS: readonly RecordingEnv[] = ['dev', 'alpha', 'pilot', 'main', 'demo'];

export function isRecordingEnv(value: string): value is RecordingEnv {
  return (RECORDING_ENVS as readonly string[]).includes(value);
}

/**
 * Path for study text-testing transcripts (manual + automatic).
 * `recordings/automatic-testing/{studyId}/{env}/{sessionId}/`
 * or `.../{studyId}/pilot/{round}/{sessionId}/` when pilotRound is set.
 */
export function getAutomaticTestingPathSegmentsForEnv(
  env: RecordingEnv,
  studyId: string,
  pilotRound?: string,
): string[] {
  if (env === 'pilot' && pilotRound) {
    return ['automatic-testing', studyId, 'pilot', pilotRound];
  }
  return ['automatic-testing', studyId, env];
}

/**
 * Path for study text-testing transcripts (manual + automatic).
 * `recordings/automatic-testing/{studyId}/{env}/...`
 */
export function getAutomaticTestingPathSegments(
  req: NextRequest,
  studyId: string,
  pilotRound?: string,
): string[] {
  return getAutomaticTestingPathSegmentsForEnv(
    getRecordingEnvFromRequest(req),
    studyId,
    pilotRound,
  );
}

/** Dev/alpha hosts use non-prod Realtime transcription models. */
export function isNonProdHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase();
  return normalized.startsWith('dev.') || normalized.startsWith('alpha.');
}
